import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { maybeCapturePosition } from "@/utils/geo";
import {
  Mic, Square, Loader2, Truck, Search, ChevronRight, Trash2,
  RotateCcw, Send, Sparkles, ArrowLeft, AlertTriangle, Gift,
} from "lucide-react";

const fmtEur = (n) =>
  (Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : null);

/* ------------------------------------------------------------------ */
/* STEP 1 — selezione cliente (dal giro o manuale)                     */
/* ------------------------------------------------------------------ */
function ClientPicker({ authAxios, onSelect }) {
  const [mode, setMode] = useState("giro"); // giro | manual
  const [giri, setGiri] = useState([]);
  const [selectedGiro, setSelectedGiro] = useState(null);
  const [stops, setStops] = useState([]);
  const [clients, setClients] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await authAxios.get("/giri");
        setGiri(r.data || []);
      } catch (err) {
        console.error("[VoiceOrders] Errore caricamento giri:", err);
      }
    })();
  }, [authAxios]);

  const openGiro = async (g) => {
    setSelectedGiro(g);
    setLoading(true);
    try {
      const r = await authAxios.get(`/giri/${g.cod_agente}/${g.cod_giorno}`);
      setStops(r.data || []);
    } catch {
      toast.error("Impossibile caricare il giro");
    } finally { setLoading(false); }
  };

  const loadClients = async () => {
    if (clients) return;
    setLoading(true);
    try {
      const r = await authAxios.get("/clients");
      setClients(r.data || []);
    } catch {
      toast.error("Impossibile caricare i clienti");
    } finally { setLoading(false); }
  };

  const q = search.trim().toLowerCase();
  const filteredStops = q
    ? stops.filter((s) => `${s.ragione_sociale} ${s.denominaz} ${s.cod_cliente}`.toLowerCase().includes(q))
    : stops;
  const filteredClients = clients && q.length >= 2
    ? clients.filter((c) => `${c.ragione_sociale} ${c.codice_cli}`.toLowerCase().includes(q)).slice(0, 30)
    : [];

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button
          data-testid="voice-tab-giro"
          onClick={() => { setMode("giro"); setSearch(""); }}
          className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition ${mode === "giro" ? "bg-amber-500 text-black" : "bg-white/5 text-zinc-300 border border-white/10"}`}
        >
          <Truck className="w-4 h-4 inline mr-1.5 -mt-0.5" />Dal giro
        </button>
        <button
          data-testid="voice-tab-manual"
          onClick={() => { setMode("manual"); setSearch(""); setSelectedGiro(null); loadClients(); }}
          className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition ${mode === "manual" ? "bg-amber-500 text-black" : "bg-white/5 text-zinc-300 border border-white/10"}`}
        >
          <Search className="w-4 h-4 inline mr-1.5 -mt-0.5" />Ricerca manuale
        </button>
      </div>

      {mode === "giro" && !selectedGiro && (
        <div className="space-y-1.5">
          {giri.length === 0 && (
            <p className="text-sm text-zinc-500 text-center py-6">Nessun giro presa ordine importato</p>
          )}
          {giri.map((g) => (
            <Card
              key={`${g.cod_agente}-${g.cod_giorno}`}
              data-testid={`voice-giro-${g.cod_agente}-${g.cod_giorno}`}
              className="bg-zinc-800 border-zinc-700 hover:border-amber-500/40 cursor-pointer transition"
              onClick={() => openGiro(g)}
            >
              <CardContent className="p-3 flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold text-sm">Giro {g.cod_agente}-{g.cod_giorno}</p>
                  <p className="text-xs text-zinc-500">{g.giorno_label} · {g.num_clienti} clienti</p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-500" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {mode === "giro" && selectedGiro && (
        <div className="space-y-1.5">
          <button
            onClick={() => { setSelectedGiro(null); setStops([]); setSearch(""); }}
            className="text-xs text-amber-400 flex items-center gap-1 mb-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" />Tutti i giri
          </button>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtra clienti del giro..."
            data-testid="voice-stop-search"
            className="bg-[#0f0f11] border-white/10 text-white"
          />
          {loading && <Loader2 className="w-5 h-5 text-amber-400 animate-spin mx-auto my-4" />}
          {filteredStops.map((s) => (
            <Card
              key={s.id}
              data-testid={`voice-stop-${s.id}`}
              className={`bg-zinc-800 border-zinc-700 transition ${s.client_id && !s.completed_order_id ? "hover:border-amber-500/40 cursor-pointer" : "opacity-50"}`}
              onClick={() => {
                if (!s.client_id) return;
                if (s.completed_order_id) {
                  toast.info("Fermata già completata: l'ordine esiste già");
                  return;
                }
                onSelect({
                  client_id: s.client_id,
                  name: s.ragione_sociale || s.denominaz_1 || s.denominaz,
                  codice_cli: s.cod_cliente,
                  sconto_canale: s.sconto_canale || 0,
                  stop_id: s.id,
                  giro: `${selectedGiro.cod_agente}-${selectedGiro.cod_giorno}`,
                  cod_agente: selectedGiro.cod_agente,
                  cod_giorno: selectedGiro.cod_giorno,
                  cod_destin: s.cod_destin ? String(s.cod_destin) : null,
                });
              }}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{s.ragione_sociale || s.denominaz}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{s.cod_cliente} · {s.indirizzo || ""} {s.localita || ""}</p>
                  </div>
                  {s.completed_order_id
                    ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 shrink-0 text-[10px]">Fatto</Badge>
                    : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {mode === "manual" && (
        <div className="space-y-1.5">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per ragione sociale o codice..."
            data-testid="voice-client-search"
            className="bg-[#0f0f11] border-white/10 text-white"
          />
          {loading && <Loader2 className="w-5 h-5 text-amber-400 animate-spin mx-auto my-4" />}
          {q.length < 2 && !loading && (
            <p className="text-xs text-zinc-500 text-center py-4">Digita almeno 2 caratteri</p>
          )}
          {filteredClients.map((c) => (
            <Card
              key={c.id}
              data-testid={`voice-client-${c.id}`}
              className="bg-zinc-800 border-zinc-700 hover:border-amber-500/40 cursor-pointer transition"
              onClick={() => onSelect({
                client_id: c.id,
                name: c.ragione_sociale,
                codice_cli: c.codice_cli,
                sconto_canale: c.sconto_canale || 0,
                stop_id: null,
                giro: null,
                cod_destin: null,
              })}
            >
              <CardContent className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{c.ragione_sociale}</p>
                  <p className="text-[11px] text-zinc-500">{c.codice_cli} · {c.citta || ""}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* STEP 2 — registrazione vocale                                       */
/* ------------------------------------------------------------------ */
function Recorder({ onAudio, processing }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const ext = type.includes("mp4") ? "mp4" : "webm";
        if (blob.size < 1000) {
          toast.error("Registrazione troppo breve, riprova");
          return;
        }
        onAudio(blob, ext);
      };
      rec.start();
      mediaRef.current = rec;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Microfono non disponibile: consenti l'accesso al microfono dal browser");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    setRecording(false);
    mediaRef.current?.stop();
  };

  useEffect(() => () => {
    clearInterval(timerRef.current);
    if (mediaRef.current?.state === "recording") mediaRef.current.stop();
  }, []);

  return (
    <div className="flex flex-col items-center py-8 gap-4">
      {!recording && !processing && (
        <>
          <button
            data-testid="voice-record-btn"
            onClick={start}
            className="w-28 h-28 rounded-full bg-amber-500 hover:bg-amber-400 active:scale-95 transition flex items-center justify-center shadow-lg shadow-amber-500/30"
          >
            <Mic className="w-12 h-12 text-black" />
          </button>
          <p className="text-sm text-zinc-400 text-center px-6">
            Tocca e detta l'ordine.<br />
            <span className="text-zinc-500 text-xs">Es: "12 ferrazzuoli, 6 strozzapreti, 5 maccheroncini e 1 omaggio"</span>
          </p>
        </>
      )}
      {recording && (
        <>
          <button
            data-testid="voice-stop-btn"
            onClick={stop}
            className="w-28 h-28 rounded-full bg-red-500 hover:bg-red-400 active:scale-95 transition flex items-center justify-center shadow-lg shadow-red-500/40 animate-pulse"
          >
            <Square className="w-10 h-10 text-white fill-white" />
          </button>
          <p className="text-lg font-mono font-bold text-red-400" data-testid="voice-timer">
            ● {String(Math.floor(seconds / 60)).padStart(1, "0")}:{String(seconds % 60).padStart(2, "0")}
          </p>
          <p className="text-sm text-zinc-400">Sto ascoltando... tocca per terminare</p>
        </>
      )}
      {processing && (
        <div className="flex flex-col items-center gap-3 py-6" data-testid="voice-processing">
          <Loader2 className="w-10 h-10 text-amber-400 animate-spin" />
          <p className="text-sm text-zinc-300">Trascrizione e interpretazione in corso...</p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* STEP 3 — revisione righe                                            */
/* ------------------------------------------------------------------ */
function ReviewRow({ row, idx, onUpdate, onRemove }) {
  const m = row.match;
  const confColor = row.confidence >= 0.85 ? "bg-emerald-400" : row.confidence >= 0.6 ? "bg-amber-400" : "bg-red-400";
  const options = m ? [m, ...(row.alternatives || [])] : (row.alternatives || []);

  return (
    <Card className={`bg-zinc-800 border ${m ? "border-zinc-700" : "border-red-500/40"}`} data-testid={`voice-row-${idx}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-2 h-2 rounded-full shrink-0 ${confColor}`} title={`Confidenza ${(row.confidence * 100).toFixed(0)}%`} />
            <p className="text-[11px] text-zinc-500 truncate">Dettato: “{row.descr}”</p>
          </div>
          <button onClick={() => onRemove(idx)} data-testid={`voice-row-remove-${idx}`} className="text-zinc-500 hover:text-red-400 p-1">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {m ? (
          <select
            value={m.product_id}
            data-testid={`voice-row-product-${idx}`}
            onChange={(e) => {
              const sel = options.find((o) => o.product_id === e.target.value);
              if (sel) onUpdate(idx, { match: sel, alternatives: options.filter((o) => o.product_id !== sel.product_id) });
            }}
            className="w-full px-2.5 py-2 rounded-md bg-[#0f0f11] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-500/40"
          >
            {options.map((o) => (
              <option key={o.product_id} value={o.product_id}>
                {o.name} ({o.sku}){o.in_storico ? " ★" : ""}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-sm text-red-300 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" />Nessun prodotto riconosciuto — rimuovi la riga
          </p>
        )}

        {m && (
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <p className="text-[10px] text-zinc-500 uppercase">Qtà</p>
              <Input
                type="number" min="0" step="1"
                value={row.qty}
                data-testid={`voice-row-qty-${idx}`}
                onChange={(e) => onUpdate(idx, { qty: e.target.value })}
                className="w-20 h-9 bg-[#0f0f11] border-white/10 text-white text-center font-mono"
              />
            </div>
            <div>
              <p className="text-[10px] text-zinc-500 uppercase flex items-center gap-1"><Gift className="w-3 h-3" />Omaggio</p>
              <Input
                type="number" min="0" step="1"
                value={row.qty_omaggio}
                data-testid={`voice-row-omaggio-${idx}`}
                onChange={(e) => onUpdate(idx, { qty_omaggio: e.target.value })}
                className="w-20 h-9 bg-[#0f0f11] border-white/10 text-white text-center font-mono"
              />
            </div>
            <div className="flex-1 min-w-[90px]">
              <p className="text-[10px] text-zinc-500 uppercase">Prezzo €</p>
              <Input
                type="number" min="0" step="0.01"
                value={row.price}
                data-testid={`voice-row-price-${idx}`}
                onChange={(e) => onUpdate(idx, { price: e.target.value, priceTouched: true })}
                className="w-full h-9 bg-[#0f0f11] border-white/10 text-white text-right font-mono"
              />
            </div>
          </div>
        )}

        {m && (m.ultimo_prezzo || m.ultima_data) && (
          <p className="text-[10px] text-zinc-500">
            Storico: {m.ultimo_prezzo ? `€ ${fmtEur(m.ultimo_prezzo)}` : "—"}
            {m.ultima_data ? ` · ultimo acquisto ${fmtDate(m.ultima_data)}` : ""}
            {m.n_volte ? ` · ${m.n_volte} volte` : ""}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* PAGINA                                                              */
/* ------------------------------------------------------------------ */
export default function VoiceOrdersPage() {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [step, setStep] = useState("client"); // client | dictate | review
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [rows, setRows] = useState([]);
  const [reparsing, setReparsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const initRows = (parsed) =>
    parsed.map((r) => {
      let price = 0;
      let touched = false;
      if (r.match) {
        if (r.price_mode === "explicit" && Number(r.price_value) > 0) {
          price = Number(r.price_value); touched = true;
        } else if (r.price_mode === "pagato" && Number(r.match.ultimo_prezzo) > 0) {
          price = r.match.ultimo_prezzo; touched = true;
        } else {
          price = Number(r.match.ultimo_prezzo) > 0 ? r.match.ultimo_prezzo : r.match.price_list;
        }
      }
      return { ...r, qty: r.qty || 1, qty_omaggio: r.qty_omaggio || 0, price, priceTouched: touched };
    });

  // Cliente scelto dal giro → dopo la dettatura apriamo direttamente la
  // schermata del Giro Ordine Rapido con le righe già selezionate.
  const goToGiroPrefill = (parsedRows, text) => {
    const matched = parsedRows.filter((r) => r.match);
    const unmatched = parsedRows.filter((r) => !r.match);
    if (matched.length === 0) {
      toast.warning("Nessun articolo riconosciuto nella dettatura");
      return false;
    }
    if (unmatched.length > 0) {
      toast.warning(`Non riconosciuti: ${unmatched.map((r) => `"${r.descr}"`).join(", ")} — aggiungili a mano nel giro`);
    }
    sessionStorage.setItem("voice_order_prefill", JSON.stringify({
      stop_id: client.stop_id,
      cod_agente: client.cod_agente,
      cod_giorno: client.cod_giorno,
      transcript: text,
      rows: matched.map((r) => ({
        sku: r.match.sku,
        product_id: r.match.product_id,
        name: r.match.name,
        unit: r.match.unit,
        iva_percentage: r.match.iva_percentage,
        price_list: r.match.price_list,
        ultimo_prezzo: r.match.ultimo_prezzo,
        macro_category: r.match.macro_category || null,
        qty: r.qty || 1,
        qty_om: r.qty_omaggio || 0,
        price_mode: r.price_mode || null,
        price_value: r.price_value || null,
      })),
    }));
    navigate("/admin/giri");
    return true;
  };

  const processAudio = useCallback(async (blob, ext) => {
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, `ordine.${ext}`);
      const t = await authAxios.post("/voice-orders/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const text = t.data.transcript;
      if (!text) {
        toast.error("Non ho capito nulla: riprova parlando più vicino al microfono");
        return;
      }
      setTranscript(text);
      const p = await authAxios.post("/voice-orders/parse", { text, client_id: client.client_id }, { timeout: 60000 });
      const parsedRows = p.data.rows || [];
      // Dal giro → vista Giro Ordine Rapido prefillata; manuale → review interna
      if (client.stop_id) {
        if (goToGiroPrefill(parsedRows, text)) return;
      }
      setRows(initRows(parsedRows));
      setStep("review");
      if (parsedRows.length === 0) {
        toast.warning("Nessun articolo riconosciuto nella dettatura");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Elaborazione fallita, riprova");
    } finally { setProcessing(false); }
  }, [authAxios, client]);

  const reparse = async () => {
    if (!transcript.trim()) return;
    setReparsing(true);
    try {
      const p = await authAxios.post("/voice-orders/parse", { text: transcript, client_id: client.client_id }, { timeout: 60000 });
      const parsedRows = p.data.rows || [];
      if (client.stop_id) {
        if (goToGiroPrefill(parsedRows, transcript)) return;
      }
      setRows(initRows(parsedRows));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Interpretazione fallita");
    } finally { setReparsing(false); }
  };

  const updateRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, ...patch };
      // cambio prodotto → riallinea il prezzo se non toccato a mano
      if (patch.match && !r.priceTouched) {
        next.price = Number(patch.match.ultimo_prezzo) > 0 ? patch.match.ultimo_prezzo : patch.match.price_list;
      }
      return next;
    }));
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const validRows = rows.filter((r) => r.match && Number(r.qty) > 0);
  const subtotal = validRows.reduce((s, r) => s + Number(r.qty) * Number(r.price || 0), 0);

  const submitOrder = async () => {
    if (validRows.length === 0) {
      toast.error("Nessuna riga valida da ordinare");
      return;
    }
    setSubmitting(true);
    try {
      const items = validRows.map((r) => ({
        product_id: r.match.product_id,
        product_name: r.match.name,
        product_sku: r.match.sku || "",
        quantity: Number(r.qty),
        qty_omaggio: Number(r.qty_omaggio) || 0,
        unit: r.match.unit || "pz",
        price_unit: Number(r.price) || 0,
        sc1: 0, sc2: 0, sc3: 0, sc4: 0,
        iva_percentage: r.match.iva_percentage || 10,
        note: null,
        _touched: r.priceTouched,
      }));

      // Stesso flusso prezzi del Giro Ordine Rapido: regole/promo dal server
      let sc = client.sconto_canale || 0;
      try {
        const resolved = await authAxios.post(`/clients/${client.client_id}/resolve-prices`, {
          product_ids: items.map((i) => i.product_id),
        });
        const prices = resolved.data.prices || {};
        sc = resolved.data.sconto_canale ?? sc;
        for (const item of items) {
          if (item._touched) continue; // prezzo modificato a mano dall'admin
          const rp = prices[item.product_id];
          if (rp?.source === "rule" || rp?.source === "promo") {
            if (rp.promo_net_fixed) {
              item.price_unit = rp.price;
              item.sc1 = 0; item.sc2 = 0; item.sc3 = 0; item.sc4 = 0;
            } else {
              item.sc1 = rp.sc1 || 0; item.sc2 = rp.sc2 || 0;
              item.sc3 = rp.sc3 || 0; item.sc4 = rp.sc4 || 0;
            }
          } else if (rp?.source === "manual") {
            item.price_unit = rp.price;
          }
        }
      } catch (err) {
        console.warn("[VoiceOrders] Resolve-prices fallback ai prezzi della review:", err);
      }
      for (const item of items) delete item._touched;

      const resp = await authAxios.post("/orders", {
        client_id: client.client_id,
        client_name: client.name,
        sconto_canale: sc,
        items,
        giro_provenienza: client.giro,
        cod_destin: client.cod_destin,
        note: `🎤 Ordine vocale: "${transcript.trim()}"`,
        ...((await maybeCapturePosition(user)) || {}),
      });
      const order = resp.data;

      if (client.stop_id) {
        try {
          await authAxios.put(`/giri/fermata/${client.stop_id}/complete`, { order_id: order.id });
        } catch (err) {
          console.warn("[VoiceOrders] Fermata complete non riuscita (non bloccante):", err);
        }
      }

      toast.success(`Ordine ${order.numero} creato!`);
      navigate(`/admin/orders/${order.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Creazione ordine fallita");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <main className="max-w-3xl mx-auto px-4 py-6 pb-28">
        <PageHeader title="Ordini Vocali" />

        {client && (
          <Card className="bg-amber-500/5 border-amber-500/20 mb-4" data-testid="voice-selected-client">
            <CardContent className="p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Cliente</p>
                <p className="text-white font-semibold text-sm truncate">{client.name}</p>
                <p className="text-[11px] text-zinc-500">
                  {client.codice_cli}{client.giro ? ` · Giro ${client.giro}` : ""}
                </p>
              </div>
              <Button
                variant="outline" size="sm"
                data-testid="voice-change-client"
                onClick={() => { setClient(null); setStep("client"); setRows([]); setTranscript(""); }}
                className="border-white/10 text-zinc-300 shrink-0"
              >
                Cambia
              </Button>
            </CardContent>
          </Card>
        )}

        {step === "client" && (
          <>
            <p className="text-sm text-zinc-400 mb-3">
              <Sparkles className="w-4 h-4 inline text-amber-400 mr-1 -mt-0.5" />
              Scegli il cliente, poi detta l'ordine: l'AI lo trasforma in righe ordine.
            </p>
            <ClientPicker
              authAxios={authAxios}
              onSelect={(c) => { setClient(c); setStep("dictate"); }}
            />
          </>
        )}

        {step === "dictate" && client && (
          <>
            <Recorder onAudio={processAudio} processing={processing} />
            {!processing && (
              <div className="text-center">
                <button
                  data-testid="voice-type-instead-btn"
                  onClick={() => { setTranscript(""); setRows([]); setStep("review"); }}
                  className="text-xs text-zinc-500 underline hover:text-zinc-300"
                >
                  oppure scrivi l'ordine a tastiera
                </button>
              </div>
            )}
          </>
        )}

        {step === "review" && (
          <div className="space-y-3">
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Trascrizione</p>
              <textarea
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                data-testid="voice-transcript"
                rows={2}
                placeholder='Es: 12 ferrazzuoli, 6 strozzapreti, 5 maccheroncini e 1 omaggio'
                className="w-full px-3 py-2 rounded-md bg-[#0f0f11] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-500/40 resize-none"
              />
              <div className="flex gap-2 mt-1.5">
                <Button
                  variant="outline" size="sm"
                  data-testid="voice-reparse-btn"
                  onClick={reparse}
                  disabled={reparsing}
                  className="border-white/10 text-zinc-300"
                >
                  {reparsing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  Reinterpreta
                </Button>
                <Button
                  variant="outline" size="sm"
                  data-testid="voice-redo-btn"
                  onClick={() => { setStep("dictate"); setRows([]); }}
                  className="border-white/10 text-zinc-300"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />Ridetta
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              {rows.map((r, i) => (
                <ReviewRow key={i} row={r} idx={i} onUpdate={updateRow} onRemove={removeRow} />
              ))}
              {rows.length === 0 && (
                <p className="text-sm text-zinc-500 text-center py-6">Nessuna riga — ridetta l'ordine</p>
              )}
            </div>
          </div>
        )}
      </main>

      {step === "review" && validRows.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-[#101012]/95 backdrop-blur border-t border-white/10 z-40">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[10px] text-zinc-500 uppercase">{validRows.length} righe · indicativo</p>
              <p className="text-lg font-bold text-white font-mono">€ {fmtEur(subtotal)}</p>
            </div>
            <Button
              data-testid="voice-submit-order-btn"
              onClick={submitOrder}
              disabled={submitting}
              className="bg-amber-500 hover:bg-amber-400 text-black font-bold h-12 px-6"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Crea Ordine
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
